/**
 * ZIP 归档层：把各类 JSON 与媒体字节打包 / 解包，并在解密后、写入前完成全部校验。
 *
 * 安全边界：
 * - 条目数量 ≤ 50,000；单条目解压后 ≤ 256 MiB；总解压后 ≤ 1 GiB；
 * - 仅允许固定路径；拒绝 `..`、绝对路径、反斜杠、空段与未登记路径；
 * - 媒体文件名必须是 `media/<sha256>.<ext>`，并逐字节校验 SHA-256 与长度；
 * - 所有 `asset:<sha256>` 引用必须能在 manifest / ZIP 中找到对应媒体。
 */
import { strFromU8, strToU8, unzipSync, zipSync } from 'fflate';
import type { Zippable } from 'fflate';

import type { LedgerEntry } from '../../core/api';
import { SERVER_PROVIDER_ID } from '../../core/providers/ids';
import type { AutomationEvent, ChatMessage, Conversation, MemoryEntry, Moment, Preset, ProviderConfig } from '../../core/types';
import type { GalleryImage } from '../gallery/galleryRepo';
import { normalizeSnapshot } from '../../core/repositories/transfer';
import { sha256Hex } from './codec';
import { isAllowedBackupStorageKey } from './local-storage';
import {
  BACKUP_MANIFEST_FORMAT,
  BACKUP_FORMAT_VERSION,
  BackupError,
  MAX_ARCHIVE_BYTES,
  MAX_ARCHIVE_ENTRIES,
  MAX_ARCHIVE_ENTRY_BYTES,
} from './types';
import type {
  BackupAccountHistory,
  BackupCounts,
  BackupLocalStorage,
  BackupManifest,
  BackupMediaEntry,
  BackupMimoAudioRecord,
  BackupMimoFile,
  BackupMimoModeRecord,
  BackupMimoReferenceRecord,
} from './types';

const COLLECTION_FILES = {
  conversations: 'data/conversations.json',
  messages: 'data/messages.json',
  presets: 'data/presets.json',
  providers: 'data/providers.json',
  memories: 'data/memories.json',
  gallery: 'data/gallery.json',
  ledger: 'data/backup-ledger.json',
  moments: 'data/moments.json',
  automationEvents: 'data/automation-events.json',
} as const;

export interface BackupCollections {
  conversations: Conversation[];
  messages: ChatMessage[];
  presets: Preset[];
  providers: ProviderConfig[];
  memories: MemoryEntry[];
  gallery: GalleryImage[];
  ledger: LedgerEntry[];
  moments: Moment[];
  automationEvents: AutomationEvent[];
}

export interface BackupArchive {
  manifest: BackupManifest;
  collections: BackupCollections;
  localStorage: BackupLocalStorage;
  mimo: BackupMimoFile;
  accountHistory: BackupAccountHistory;
  media: Map<string, { bytes: Uint8Array; mime: string; ext: string }>;
}

export interface ArchivePayload {
  manifest: BackupManifest;
  collections: BackupCollections;
  localStorage: BackupLocalStorage;
  mimo: BackupMimoFile;
  accountHistory: BackupAccountHistory;
  media: Map<string, Uint8Array>;
}

const JSON_FILES = [
  'manifest.json',
  COLLECTION_FILES.conversations,
  COLLECTION_FILES.messages,
  COLLECTION_FILES.presets,
  COLLECTION_FILES.providers,
  COLLECTION_FILES.memories,
  COLLECTION_FILES.gallery,
  COLLECTION_FILES.ledger,
  'settings/local-storage.json',
  'tts/mimo.json',
  'account/history.json',
] as const;

const OPTIONAL_JSON_FILES = [
  COLLECTION_FILES.moments,
  COLLECTION_FILES.automationEvents,
] as const;

const MEDIA_NAME = /^media\/([a-f0-9]{64})\.([a-z0-9]{1,8})$/;
const SHA256 = /^[a-f0-9]{64}$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function fail(path: string, reason: string): never {
  throw new BackupError('VALIDATION', `${path}：${reason}`);
}

function record(value: unknown, path: string): Record<string, unknown> {
  if (!isRecord(value)) fail(path, '期望 object');
  return value;
}

function array(value: unknown, path: string): unknown[] {
  if (!Array.isArray(value)) fail(path, '期望 array');
  return value;
}

function stringValue(source: Record<string, unknown>, field: string, path: string): string {
  const value = source[field];
  if (typeof value !== 'string') fail(`${path}.${field}`, '期望 string');
  return value;
}

function optionalString(
  source: Record<string, unknown>,
  field: string,
  path: string,
): string | undefined {
  const value = source[field];
  if (value === undefined) return undefined;
  if (typeof value !== 'string') fail(`${path}.${field}`, '期望 string');
  return value;
}

function nullableString(
  source: Record<string, unknown>,
  field: string,
  path: string,
): string | null {
  const value = source[field];
  if (value === undefined || value === null) return null;
  if (typeof value !== 'string') fail(`${path}.${field}`, '期望 string 或 null');
  return value;
}

function numberValue(source: Record<string, unknown>, field: string, path: string): number {
  const value = source[field];
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    fail(`${path}.${field}`, '期望有限数字');
  }
  return value;
}

function booleanValue(source: Record<string, unknown>, field: string, path: string): boolean {
  const value = source[field];
  if (typeof value !== 'boolean') fail(`${path}.${field}`, '期望 boolean');
  return value;
}

function enumValue<T extends string>(
  source: Record<string, unknown>,
  field: string,
  allowed: readonly T[],
  path: string,
): T {
  const value = stringValue(source, field, path);
  if (!(allowed as readonly string[]).includes(value)) {
    fail(`${path}.${field}`, `取值非法：${value}`);
  }
  return value as T;
}

function parseJson(files: Record<string, Uint8Array>, name: string): unknown {
  const bytes = files[name];
  if (bytes === undefined) throw new BackupError('FORMAT', `备份缺少 ${name}`);
  try {
    return JSON.parse(strFromU8(bytes));
  } catch {
    throw new BackupError('FORMAT', `${name} 不是合法 JSON`);
  }
}

function safeEntryName(name: string): boolean {
  if (name.includes('\\') || name.includes('\0') || name.startsWith('/')) return false;
  const segments = name.split('/');
  if (segments.some((segment) => segment === '' || segment === '.' || segment === '..')) {
    return false;
  }
  if ((JSON_FILES as readonly string[]).includes(name)) return true;
  if ((OPTIONAL_JSON_FILES as readonly string[]).includes(name)) return true;
  return MEDIA_NAME.test(name);
}

/** 解压并执行条目数量 / 大小 / 路径安全检查。 */
function unzipArchive(zip: Uint8Array): Record<string, Uint8Array> {
  let count = 0;
  let total = 0;
  const seen = new Set<string>();
  const rejected: string[] = [];
  const limitErrors: BackupError[] = [];
  const files: Record<string, Uint8Array> = {};
  try {
    const unzipped = unzipSync(zip, {
      filter: (file) => {
        if (file.name.endsWith('/')) return false;
        count += 1;
        if (count > MAX_ARCHIVE_ENTRIES) {
          limitErrors.push(new BackupError('LIMIT', `备份条目数量超过上限 ${MAX_ARCHIVE_ENTRIES}`));
          return false;
        }
        if (!safeEntryName(file.name)) {
          rejected.push(file.name);
          return false;
        }
        if (seen.has(file.name)) {
          rejected.push(file.name);
          return false;
        }
        seen.add(file.name);
        if (!Number.isFinite(file.originalSize) || file.originalSize < 0) {
          rejected.push(file.name);
          return false;
        }
        if (file.originalSize > MAX_ARCHIVE_ENTRY_BYTES) {
          limitErrors.push(new BackupError('LIMIT', `条目过大：${file.name}`));
          return false;
        }
        total += file.originalSize;
        if (total > MAX_ARCHIVE_BYTES) {
          limitErrors.push(
            new BackupError('LIMIT', `备份解压后超过 ${MAX_ARCHIVE_BYTES} 字节`),
          );
          return false;
        }
        return true;
      },
    });
    const limitError = limitErrors[0];
    if (limitError !== undefined) throw limitError;
    if (rejected.length > 0) {
      throw new BackupError('VALIDATION', `备份包含非法或重复路径：${rejected[0] ?? ''}`);
    }
    for (const [name, bytes] of Object.entries(unzipped)) files[name] = bytes;
  } catch (error) {
    if (error instanceof BackupError) throw error;
    throw new BackupError('FORMAT', '备份 ZIP 已损坏或无法读取');
  }
  return files;
}

function parseManifest(value: unknown): BackupManifest {
  const source = record(value, 'manifest.json');
  if (source.format !== BACKUP_MANIFEST_FORMAT) {
    throw new BackupError('FORMAT', '备份 manifest 格式不匹配');
  }
  const version = numberValue(source, 'version', 'manifest.json');
  if (version !== 1 && version !== BACKUP_FORMAT_VERSION) {
    throw new BackupError('FORMAT', `不支持的备份版本：${version}`);
  }
  const account = record(source.account, 'manifest.json.account');
  const media = record(source.media, 'manifest.json.media');
  const ledger = record(source.ledger, 'manifest.json.ledger');
  const warningsRaw = source.warnings === undefined ? [] : array(source.warnings, 'manifest.json.warnings');
  const warnings = warningsRaw.map((item, index) => {
    if (typeof item !== 'string') fail(`manifest.json.warnings[${index}]`, '期望 string');
    return item;
  });
  const entries = array(media.entries, 'manifest.json.media.entries').map((item, index) => {
    const path = `manifest.json.media.entries[${index}]`;
    const entry = record(item, path);
    const sha256 = stringValue(entry, 'sha256', path);
    if (!SHA256.test(sha256)) fail(`${path}.sha256`, '不是合法 SHA-256');
    const mime = stringValue(entry, 'mime', path);
    if (mime.trim() === '') fail(`${path}.mime`, '不能为空');
    const bytes = numberValue(entry, 'bytes', path);
    if (!Number.isInteger(bytes) || bytes < 0) fail(`${path}.bytes`, '必须是非负整数');
    const ext = stringValue(entry, 'ext', path);
    if (!/^[a-z0-9]{1,8}$/.test(ext)) fail(`${path}.ext`, '扩展名非法');
    return { sha256, mime, bytes, ext };
  });
  const countsRaw = record(source.counts, 'manifest.json.counts');
  const countKeys: (keyof BackupCounts)[] = [
    'conversations',
    'messages',
    'presets',
    'providers',
    'memories',
    'gallery',
    'media',
    'localStorageKeys',
    'ledger',
    'ttsAudio',
    'ttsReferences',
    'moments',
    'automationEvents',
  ];
  const counts = {} as BackupCounts;
  for (const key of countKeys) {
    const value = countsRaw[key] ?? 0;
    if (typeof value !== 'number' || !Number.isInteger(value) || value < 0) {
      fail(`manifest.json.counts.${key}`, '必须是非负整数');
    }
    counts[key] = value;
  }
  const backupId = stringValue(source, 'backupId', 'manifest.json');
  if (backupId.trim() === '') fail('manifest.json.backupId', '不能为空');
  const exportedAt = numberValue(source, 'exportedAt', 'manifest.json');
  const capturedAt = ledger.capturedAt;
  const ledgerWarning = optionalString(ledger, 'warning', 'manifest.json.ledger');
  return {
    format: BACKUP_MANIFEST_FORMAT,
    version,
    backupId,
    exportedAt,
    account: {
      id: stringValue(account, 'id', 'manifest.json.account'),
      email: stringValue(account, 'email', 'manifest.json.account'),
    },
    counts,
    media: {
      count: numberValue(media, 'count', 'manifest.json.media'),
      bytes: numberValue(media, 'bytes', 'manifest.json.media'),
      entries,
    },
    ledger: {
      included: booleanValue(ledger, 'included', 'manifest.json.ledger'),
      count: numberValue(ledger, 'count', 'manifest.json.ledger'),
      capturedAt:
        capturedAt === null
          ? null
          : typeof capturedAt === 'number' && Number.isFinite(capturedAt)
            ? capturedAt
            : fail('manifest.json.ledger.capturedAt', '期望 number 或 null'),
      ...(ledgerWarning === undefined ? {} : { warning: ledgerWarning }),
    },
    warnings,
  };
}

function normalizeGalleryImage(value: unknown, index: number): GalleryImage {
  const path = `data/gallery.json[${index}]`;
  const source = record(value, path);
  const image: GalleryImage = {
    id: stringValue(source, 'id', path),
    dataUrl: stringValue(source, 'dataUrl', path),
    mime: stringValue(source, 'mime', path),
    prompt: stringValue(source, 'prompt', path),
    model: stringValue(source, 'model', path),
    createdAt: numberValue(source, 'createdAt', path),
    favorite: booleanValue(source, 'favorite', path),
  };
  const revisedPrompt = optionalString(source, 'revisedPrompt', path);
  if (revisedPrompt !== undefined) image.revisedPrompt = revisedPrompt;
  for (const field of ['presetId', 'conversationId', 'userId'] as const) {
    const value = optionalString(source, field, path);
    if (value !== undefined) image[field] = value;
  }
  return image;
}

function normalizeMoment(value: unknown, index: number): Moment {
  const path = `data/moments.json[${index}]`;
  const source = record(value, path);
  const moment: Moment = {
    id: stringValue(source, 'id', path),
    presetId: stringValue(source, 'presetId', path),
    content: stringValue(source, 'content', path),
    images: array(source.images, `${path}.images`).map((item, imageIndex) => {
      if (typeof item !== 'string') fail(`${path}.images[${imageIndex}]`, '期望 string');
      return item;
    }),
    createdAt: numberValue(source, 'createdAt', path),
    authorName: stringValue(source, 'authorName', path),
  };
  const avatar = optionalString(source, 'authorAvatar', path);
  const sourceMessageId = optionalString(source, 'sourceMessageId', path);
  const userId = optionalString(source, 'userId', path);
  if (avatar !== undefined) moment.authorAvatar = avatar;
  if (sourceMessageId !== undefined) moment.sourceMessageId = sourceMessageId;
  if (userId !== undefined) moment.userId = userId;
  if (source.galleryImported !== undefined) moment.galleryImported = booleanValue(source, 'galleryImported', path);
  return moment;
}

function normalizeAutomationEvent(value: unknown, index: number): AutomationEvent {
  const path = `data/automation-events.json[${index}]`;
  const source = record(value, path);
  const event: AutomationEvent = {
    id: stringValue(source, 'id', path),
    presetId: stringValue(source, 'presetId', path),
    kind: enumValue(source, 'kind', ['bubble', 'moment', 'moment_check'] as const, path),
    dueAt: numberValue(source, 'dueAt', path),
    status: enumValue(source, 'status', ['queued', 'running', 'done', 'error', 'skipped'] as const, path),
    createdAt: numberValue(source, 'createdAt', path),
    updatedAt: numberValue(source, 'updatedAt', path),
  };
  const sourceMessageId = optionalString(source, 'sourceMessageId', path);
  const error = optionalString(source, 'error', path);
  const userId = optionalString(source, 'userId', path);
  if (sourceMessageId !== undefined) event.sourceMessageId = sourceMessageId;
  if (error !== undefined) event.error = error;
  if (userId !== undefined) event.userId = userId;
  return event;
}

function normalizeLedgerEntry(value: unknown, index: number): LedgerEntry {
  const path = `data/backup-ledger.json[${index}]`;
  const source = record(value, path);
  return {
    id: stringValue(source, 'id', path),
    userId: stringValue(source, 'userId', path),
    type: stringValue(source, 'type', path),
    amountUnits: numberValue(source, 'amountUnits', path),
    balanceAfterUnits: numberValue(source, 'balanceAfterUnits', path),
    referenceType: nullableString(source, 'referenceType', path),
    referenceId: nullableString(source, 'referenceId', path),
    description: stringValue(source, 'description', path),
    metadata: source.metadata ?? null,
    createdAt: stringValue(source, 'createdAt', path),
    updatedAt: stringValue(source, 'updatedAt', path),
  };
}

function normalizeLocalStorage(value: unknown): BackupLocalStorage {
  const source = record(value, 'settings/local-storage.json');
  const values: BackupLocalStorage = {};
  const entries = Object.entries(source);
  if (entries.length > 5_000) fail('settings/local-storage.json', '键数量超过上限');
  let total = 0;
  for (const [key, item] of entries) {
    if (key.length === 0 || key.length > 512 || key.includes('\0')) {
      fail('settings/local-storage.json', `非法键名：${key.slice(0, 80)}`);
    }
    if (typeof item !== 'string') fail(`settings/local-storage.json.${key}`, '期望 string');
    total += key.length + item.length;
    if (total > 8 * 1024 * 1024) fail('settings/local-storage.json', '内容超过 8 MiB');
    values[key] = item;
  }
  return values;
}

function normalizeMimo(value: unknown): BackupMimoFile {
  const source = record(value, 'tts/mimo.json');
  const audio = array(source.audio, 'tts/mimo.json.audio').map((item, index) => {
    const path = `tts/mimo.json.audio[${index}]`;
    const entry = record(item, path);
    const audioRecord: BackupMimoAudioRecord = {
      key: stringValue(entry, 'key', path),
      id: stringValue(entry, 'id', path),
      sha256: stringValue(entry, 'sha256', path),
      bytes: numberValue(entry, 'bytes', path),
      mime: stringValue(entry, 'mime', path),
      links: array(entry.links, `${path}.links`).map((link, linkIndex) => {
        if (typeof link !== 'string') fail(`${path}.links[${linkIndex}]`, '期望 string');
        return link;
      }),
      preview: booleanValue(entry, 'preview', path),
      createdAt: numberValue(entry, 'createdAt', path),
      mediaRef: stringValue(entry, 'mediaRef', path),
    };
    if (!SHA256.test(audioRecord.sha256)) fail(`${path}.sha256`, '不是合法 SHA-256');
    if (audioRecord.mime !== 'audio/wav') fail(`${path}.mime`, 'TTS 音频必须为 audio/wav');
    return audioRecord;
  });
  let reference: BackupMimoReferenceRecord | undefined;
  if (source.reference !== undefined) {
    const path = 'tts/mimo.json.reference';
    const entry = record(source.reference, path);
    const consent = entry.consent;
    if (consent !== true) fail(`${path}.consent`, '参考音频必须带明确授权');
    reference = {
      name: stringValue(entry, 'name', path),
      sha256: stringValue(entry, 'sha256', path),
      consent: true,
      mime: stringValue(entry, 'mime', path),
      mediaRef: stringValue(entry, 'mediaRef', path),
    };
    if (!SHA256.test(reference.sha256)) fail(`${path}.sha256`, '不是合法 SHA-256');
  }
  const modes = array(source.modes, 'tts/mimo.json.modes').map((item, index) => {
    const path = `tts/mimo.json.modes[${index}]`;
    const entry = record(item, path);
    const mode = enumValue(entry, 'mode', ['off', 'preset', 'design', 'clone'] as const, path);
    return { scope: stringValue(entry, 'scope', path), mode } satisfies BackupMimoModeRecord;
  });
  let serverPreferences: BackupMimoFile['serverPreferences'];
  if (source.serverPreferences !== undefined) {
    serverPreferences = record(source.serverPreferences, 'tts/mimo.json.serverPreferences') as unknown as BackupMimoFile['serverPreferences'];
  }
  return {
    audio,
    modes,
    ...(reference === undefined ? {} : { reference }),
    ...(serverPreferences === undefined ? {} : { serverPreferences }),
  };
}

function normalizeAccountHistory(value: unknown): BackupAccountHistory {
  const source = record(value, 'account/history.json');
  const capturedAt = source.capturedAt;
  if (capturedAt !== null && (typeof capturedAt !== 'number' || !Number.isFinite(capturedAt))) {
    fail('account/history.json.capturedAt', '期望 number 或 null');
  }
  const included = booleanValue(source, 'included', 'account/history.json');
  const warning = optionalString(source, 'warning', 'account/history.json');
  const wallet = source.wallet;
  if (wallet !== null && wallet !== undefined && !isRecord(wallet)) {
    fail('account/history.json.wallet', '期望 object 或 null');
  }
  const entries = array(source.entries, 'account/history.json.entries').map((item, index) => {
    const path = `account/history.json.entries[${index}]`;
    const entry = record(item, path);
    return {
      id: stringValue(entry, 'id', path),
      userId: stringValue(entry, 'userId', path),
      type: stringValue(entry, 'type', path),
      amountUnits: numberValue(entry, 'amountUnits', path),
      balanceAfterUnits: numberValue(entry, 'balanceAfterUnits', path),
      referenceType: nullableString(entry, 'referenceType', path),
      referenceId: nullableString(entry, 'referenceId', path),
      description: stringValue(entry, 'description', path),
      metadata: entry.metadata ?? null,
      createdAt: stringValue(entry, 'createdAt', path),
      updatedAt: stringValue(entry, 'updatedAt', path),
    } satisfies LedgerEntry;
  });
  return {
    capturedAt: capturedAt ?? null,
    included,
    entries,
    wallet: wallet === undefined ? null : (wallet as BackupAccountHistory['wallet']),
    ...(warning === undefined ? {} : { warning }),
  };
}

function parseCollections(files: Record<string, Uint8Array>): BackupCollections {
  const raw = {
    conversations: parseJson(files, COLLECTION_FILES.conversations),
    messages: parseJson(files, COLLECTION_FILES.messages),
    presets: parseJson(files, COLLECTION_FILES.presets),
    providers: parseJson(files, COLLECTION_FILES.providers),
    memories: parseJson(files, COLLECTION_FILES.memories),
  };
  let snapshot;
  try {
    snapshot = normalizeSnapshot(raw);
  } catch (error) {
    throw new BackupError(
      'VALIDATION',
      error instanceof Error ? error.message : '备份数据结构不合法',
    );
  }
  const galleryRaw = array(parseJson(files, COLLECTION_FILES.gallery), COLLECTION_FILES.gallery);
  const ledgerRaw = array(parseJson(files, COLLECTION_FILES.ledger), COLLECTION_FILES.ledger);
  const momentsRaw = files[COLLECTION_FILES.moments] === undefined ? [] : array(parseJson(files, COLLECTION_FILES.moments), COLLECTION_FILES.moments);
  const eventsRaw = files[COLLECTION_FILES.automationEvents] === undefined ? [] : array(parseJson(files, COLLECTION_FILES.automationEvents), COLLECTION_FILES.automationEvents);
  return {
    conversations: snapshot.conversations,
    messages: snapshot.messages,
    presets: snapshot.presets,
    providers: snapshot.providers,
    memories: snapshot.memories,
    gallery: galleryRaw.map(normalizeGalleryImage),
    ledger: ledgerRaw.map(normalizeLedgerEntry),
    moments: momentsRaw.map(normalizeMoment),
    automationEvents: eventsRaw.map(normalizeAutomationEvent),
  };
}

async function validateMedia(
  files: Record<string, Uint8Array>,
  manifest: BackupManifest,
): Promise<Map<string, { bytes: Uint8Array; mime: string; ext: string }>> {
  const entries = new Map<string, BackupMediaEntry>();
  for (const entry of manifest.media.entries) {
    if (entries.has(entry.sha256)) {
      throw new BackupError('VALIDATION', `manifest 中媒体哈希重复：${entry.sha256}`);
    }
    entries.set(entry.sha256, entry);
  }
  const media = new Map<string, { bytes: Uint8Array; mime: string; ext: string }>();
  let total = 0;
  for (const [name, bytes] of Object.entries(files)) {
    if (!name.startsWith('media/')) continue;
    const match = MEDIA_NAME.exec(name);
    if (match === null) throw new BackupError('VALIDATION', `非法媒体路径：${name}`);
    const hash = match[1] ?? '';
    const ext = match[2] ?? '';
    const entry = entries.get(hash);
    if (entry === undefined) throw new BackupError('VALIDATION', `媒体未在 manifest 登记：${hash}`);
    if (entry.ext !== ext) throw new BackupError('VALIDATION', `媒体扩展名不匹配：${hash}`);
    if (bytes.length !== entry.bytes) {
      throw new BackupError('VALIDATION', `媒体字节数不符：${hash}`);
    }
    const actual = await sha256Hex(bytes);
    if (actual !== hash) throw new BackupError('VALIDATION', `媒体哈希不符：${hash}`);
    total += bytes.length;
    media.set(hash, { bytes, mime: entry.mime, ext });
  }
  for (const entry of manifest.media.entries) {
    if (!media.has(entry.sha256)) {
      throw new BackupError('VALIDATION', `备份缺少媒体文件：${entry.sha256}`);
    }
  }
  if (manifest.media.count !== media.size || manifest.media.bytes !== total) {
    throw new BackupError('VALIDATION', 'manifest 中的媒体数量 / 字节数不匹配');
  }
  return media;
}

function assertAssetRefs(value: unknown, media: ReadonlyMap<string, unknown>): void {
  const stack: unknown[] = [value];
  while (stack.length > 0) {
    const item = stack.pop();
    if (typeof item === 'string') {
      if (!item.startsWith('asset:')) continue;
      const hash = item.slice('asset:'.length);
      if (!SHA256.test(hash)) throw new BackupError('VALIDATION', `非法资源引用：${item}`);
      if (!media.has(hash)) throw new BackupError('VALIDATION', `资源引用缺失：${hash}`);
    } else if (Array.isArray(item)) {
      for (const child of item) stack.push(child);
    } else if (isRecord(item)) {
      for (const child of Object.values(item)) stack.push(child);
    }
  }
}

function validateReferences(
  collections: BackupCollections,
  mimo: BackupMimoFile,
  accountHistory: BackupAccountHistory,
  accountId: string,
): string[] {
  const conversationIds = new Set(collections.conversations.map((item) => item.id));
  const presetIds = new Set(collections.presets.map((item) => item.id));
  const providerIds = new Set(collections.providers.map((item) => item.id));
  const missingPresetConversations = new Set<string>();
  const missingProviderConversations = new Set<string>();
  for (const message of collections.messages) {
    if (!conversationIds.has(message.conversationId)) {
      throw new BackupError('VALIDATION', `消息 ${message.id} 引用了不存在的会话`);
    }
  }
  for (const conversation of collections.conversations) {
    if (conversation.presetId !== undefined && !presetIds.has(conversation.presetId)) {
      missingPresetConversations.add(conversation.id);
    }
    if (
      conversation.providerId !== undefined &&
      conversation.providerId !== SERVER_PROVIDER_ID &&
      !providerIds.has(conversation.providerId)
    ) {
      missingProviderConversations.add(conversation.id);
      // 提供商已在导出前删除时，保留绑定只会让会话选中一个不存在的项；
      // 清掉失效绑定与模型，让发送逻辑回退到当前默认提供商。
      delete conversation.providerId;
      delete conversation.model;
      delete conversation.modelKind;
    }
  }
  const messageIds = new Set(collections.messages.map((item) => item.id));
  for (const audio of mimo.audio) {
    for (const link of audio.links) {
      if (!messageIds.has(link)) {
        throw new BackupError('VALIDATION', `TTS 音频 ${audio.key} 关联了不存在的消息 ${link}`);
      }
    }
  }
  for (const mode of mimo.modes) {
    try {
      const parsed: unknown = JSON.parse(mode.scope);
      if (!Array.isArray(parsed) || parsed[0] !== accountId) {
        throw new BackupError('VALIDATION', 'TTS 模式不属于备份账号');
      }
    } catch {
      throw new BackupError('VALIDATION', 'TTS 模式 scope 非法');
    }
  }
  for (const entry of accountHistory.entries) {
    if (entry.userId !== accountId) {
      throw new BackupError('VALIDATION', '余额流水不属于备份账号');
    }
  }

  const warnings: string[] = [];
  for (const moment of collections.moments) {
    if (moment.presetId && !presetIds.has(moment.presetId)) {
      warnings.push(`朋友圈 ${moment.id} 的角色卡不在备份中，将保留作者快照`);
    }
  }
  for (const event of collections.automationEvents) {
    if (!presetIds.has(event.presetId)) {
      warnings.push(`自动化待办 ${event.id} 的角色卡不在备份中，将跳过`);
    }
  }
  if (missingPresetConversations.size > 0) {
    warnings.push(
      `${missingPresetConversations.size} 个会话引用了未包含在备份中的角色卡；会话已保留，将按无角色卡打开`,
    );
  }
  if (missingProviderConversations.size > 0) {
    warnings.push(
      `${missingProviderConversations.size} 个会话引用了未包含在备份中的提供商；会话已保留并解除失效绑定，发送时会使用当前可用的提供商`,
    );
  }
  return warnings;
}

function computeCounts(
  collections: BackupCollections,
  localStorage: BackupLocalStorage,
  mimo: BackupMimoFile,
  media: Map<string, unknown>,
  ledger: LedgerEntry[],
): BackupCounts {
  return {
    conversations: collections.conversations.length,
    messages: collections.messages.length,
    presets: collections.presets.length,
    providers: collections.providers.length,
    memories: collections.memories.length,
    gallery: collections.gallery.length,
    media: media.size,
    localStorageKeys: Object.keys(localStorage).length,
    ledger: ledger.length,
    ttsAudio: mimo.audio.length,
    ttsReferences: mimo.reference === undefined ? 0 : 1,
    moments: collections.moments.length,
    automationEvents: collections.automationEvents.length,
  };
}

/** 打包：JSON 压缩、媒体原样存储；manifest 必须与实际媒体一致。 */
export function packArchive(payload: ArchivePayload): Uint8Array {
  const entries: Zippable = {};
  const jsonEntries: Record<string, unknown> = {
    'manifest.json': payload.manifest,
    [COLLECTION_FILES.conversations]: payload.collections.conversations,
    [COLLECTION_FILES.messages]: payload.collections.messages,
    [COLLECTION_FILES.presets]: payload.collections.presets,
    [COLLECTION_FILES.providers]: payload.collections.providers,
    [COLLECTION_FILES.memories]: payload.collections.memories,
    [COLLECTION_FILES.gallery]: payload.collections.gallery,
    [COLLECTION_FILES.ledger]: payload.collections.ledger,
    [COLLECTION_FILES.moments]: payload.collections.moments,
    [COLLECTION_FILES.automationEvents]: payload.collections.automationEvents,
    'settings/local-storage.json': payload.localStorage,
    'tts/mimo.json': payload.mimo,
    'account/history.json': payload.accountHistory,
  };
  for (const [name, value] of Object.entries(jsonEntries)) {
    // 重新包一层本 realm 的 Uint8Array：jsdom 的 TextEncoder 可能返回跨 realm 视图，
    // fflate 的 `instanceof Uint8Array` 会因此把字节数组误当成嵌套目录。
    const bytes = new Uint8Array(strToU8(JSON.stringify(value, null, 2)));
    entries[name] = [bytes, { level: 6 }];
  }
  for (const entry of payload.manifest.media.entries) {
    const bytes = payload.media.get(entry.sha256);
    if (bytes === undefined) throw new BackupError('FORMAT', `缺少媒体：${entry.sha256}`);
    if (bytes.length !== entry.bytes) throw new BackupError('FORMAT', `媒体大小不符：${entry.sha256}`);
    entries[`media/${entry.sha256}.${entry.ext}`] = [bytes, { level: 0 }];
  }
  return zipSync(entries);
}

/** 解包 + 全量校验；失败时不返回任何可写入的数据。 */
export async function unpackArchive(zip: Uint8Array): Promise<BackupArchive> {
  const files = unzipArchive(zip);
  for (const name of JSON_FILES) {
    if (files[name] === undefined) throw new BackupError('FORMAT', `备份缺少 ${name}`);
  }
  const manifest = parseManifest(parseJson(files, 'manifest.json'));
  const media = await validateMedia(files, manifest);
  const collections = parseCollections(files);
  const localStorage = normalizeLocalStorage(parseJson(files, 'settings/local-storage.json'));
  const mimo = normalizeMimo(parseJson(files, 'tts/mimo.json'));
  const accountHistory = normalizeAccountHistory(parseJson(files, 'account/history.json'));
  assertAssetRefs(collections.messages, media);
  assertAssetRefs(collections.presets, media);
  assertAssetRefs(collections.gallery, media);
  assertAssetRefs(collections.moments, media);
  assertAssetRefs(mimo, media);
  const referenceWarnings = validateReferences(
    collections,
    mimo,
    accountHistory,
    manifest.account.id,
  );
  for (const key of Object.keys(localStorage)) {
    if (!isAllowedBackupStorageKey(key, manifest.account.id)) {
      throw new BackupError('VALIDATION', `备份包含不允许恢复的本地键：${key}`);
    }
  }
  const counts = computeCounts(
    collections,
    localStorage,
    mimo,
    media,
    accountHistory.entries,
  );
  const warnings = [...manifest.warnings, ...referenceWarnings];
  if (JSON.stringify(counts) !== JSON.stringify(manifest.counts)) {
    warnings.push('manifest 计数与实际内容不一致，已按实际内容恢复');
  }
  if (manifest.ledger.included !== accountHistory.included) {
    warnings.push('流水快照状态不一致，已按实际内容处理');
  }
  const normalizedManifest: BackupManifest = {
    ...manifest,
    counts,
    media: { count: media.size, bytes: manifest.media.bytes, entries: manifest.media.entries },
    ledger: {
      included: accountHistory.included,
      count: accountHistory.entries.length,
      capturedAt: accountHistory.capturedAt,
      ...(accountHistory.warning === undefined
        ? manifest.ledger.warning === undefined
          ? {}
          : { warning: manifest.ledger.warning }
        : { warning: accountHistory.warning }),
    },
    warnings,
  };
  return {
    manifest: normalizedManifest,
    collections,
    localStorage,
    mimo,
    accountHistory,
    media,
  };
}

/** 仅测试用：保留空实现以兼容旧测试清理调用。 */
export function clearDigestCacheForTest(): void {
  // 媒体哈希现在按文件即时计算，不再维护跨调用缓存。
}
